import React, { useEffect, useState } from "react";
import { Building2, ArrowUpRight } from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import { useBranch } from "../../contexts/BranchContext";
import { convertToIndianCurrency } from "../../utils/common";

/**
 * Branch comparison — one row per branch, for the institute-wide view.
 *
 * This is the counterpart to the rest of the dashboard, not a duplicate of it:
 * every other card scopes to the ONE branch selected in the top nav and answers
 * "how is this branch doing". Only this table answers "which of my branches is
 * behind", which is the question a multi-centre owner actually opens the
 * dashboard with.
 *
 * Laid out as a dense horizontally-scrolling table rather than a grid of cards
 * because vertical space is the scarce resource on the 15.6" @150% (~1280×720)
 * admin viewport — ten branches as cards would push everything else off-screen.
 */
/*
 * Converted to utilities in Phase 5; branch-comparison.scss deleted in the same
 * commit (TOKEN_CONTRACT §6.1).
 *
 * The table's `th, td { … }` block is an arbitrary variant on the table element
 * rather than a class on every cell — the cells are generated in three separate
 * loops (head, body, foot) and a per-cell class would have to be threaded through
 * all three. The two sticky-column and alignment classes ARE per-cell, because
 * they genuinely differ per cell.
 *
 * ⚠️ `text-end` is BOTH a Bootstrap and a Tailwind class here, which is exactly
 * the collision the `tw-` prefix exists for (audit F15). It is `tw-text-end` now.
 */
const CELL =
  "[&_:is(th,td)]:tw-whitespace-nowrap [&_:is(th,td)]:tw-border-b [&_:is(th,td)]:tw-border-line " +
  "[&_:is(th,td)]:tw-px-2.5 [&_:is(th,td)]:tw-py-[7px] " +
  "[&_th]:tw-text-[0.72rem] [&_th]:tw-font-semibold [&_th]:tw-uppercase [&_th]:tw-tracking-[0.03em] [&_th]:tw-text-content-muted " +
  "[&_td]:tw-text-content [&_tbody_tr:hover_td]:tw-bg-surface-alt " +
  "[&_tfoot_td]:tw-border-b-0 [&_tfoot_td]:tw-border-t-2 [&_tfoot_td]:tw-font-semibold";

// Sticky cells sit above scrolling content, so they need an opaque ground of
// their own or the numbers slide visibly underneath the branch name.
const NAMECOL = "tw-sticky tw-left-0 tw-z-[1] tw-bg-surface-raised tw-text-left";

const CARD = "tw-mb-4 tw-rounded-[12px] tw-border tw-border-line tw-bg-surface-raised tw-px-4 tw-py-3.5";
const HEAD = "tw-mb-2.5 tw-flex tw-items-center tw-gap-2 tw-text-content";
const TITLE = "tw-m-0 tw-text-[0.95rem] tw-font-semibold";

const COLUMNS = [
  { key: "students", label: "Students", align: "end" },
  { key: "newStudents", label: "New", align: "end", hint: "Admitted in the selected range" },
  { key: "activeBatches", label: "Batches", align: "end", hint: "Upcoming + ongoing" },
  { key: "enquiries", label: "Enquiries", align: "end" },
  { key: "conversionRate", label: "Conv.", align: "end", format: "pct" },
  { key: "collected", label: "Collected", align: "end", format: "money" },
  { key: "pendingFees", label: "Outstanding", align: "end", format: "money", hint: "Total balance, not limited to the range" },
];

const fmt = (value, format) => {
  if (value === null || value === undefined) return "—";
  if (format === "money") return convertToIndianCurrency(value);
  if (format === "pct") return `${value}%`;
  return value.toLocaleString("en-IN");
};

const BranchComparison = ({ from, to }) => {
  const { isMultiBranch, branches, canSelectAll, selectBranch } = useBranch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Nothing to compare unless the caller can actually see more than one branch.
  const eligible = isMultiBranch && canSelectAll && branches.length > 1;

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        // Explicitly unscoped: this table is about every branch, so it must not
        // inherit the currently selected branch from the default header.
        const res = await apiClient.get("/branches/stats", {
          params: { from, to },
          headers: { "X-Branch-Id": "all" },
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [eligible, from, to]);

  if (!eligible) return null;
  if (failed) return null;

  if (loading) {
    return (
      <div className={CARD}>
        <div className={HEAD}>
          <Building2 size={16} />
          <h3 className={TITLE}>Branch comparison</h3>
        </div>
        <Shimmer height={38} count={5} />
      </div>
    );
  }

  const rows = data?.branches || [];
  if (rows.length < 2) return null;

  const totals = data?.totals || {};
  // Highlight the best performer per column so a ten-branch table can be read
  // at a glance instead of column by column.
  const best = {};
  for (const c of COLUMNS) {
    const vals = rows.map((r) => r[c.key]).filter((v) => typeof v === "number");
    if (vals.length) best[c.key] = Math.max(...vals);
  }

  return (
    <div className={CARD}>
      <div className={HEAD}>
        <Building2 size={16} />
        <h3 className={TITLE}>Branch comparison</h3>
        <span className="tw-ml-auto tw-text-[0.75rem] tw-text-content-muted">{rows.length} branches</span>
      </div>

      <div className="tw-overflow-x-auto">
        <table className={`tw-w-full tw-min-w-[720px] tw-border-collapse tw-text-[0.82rem] ${CELL}`}>
          <thead>
            <tr>
              <th className={NAMECOL}>Branch</th>
              {COLUMNS.map((c) => (
                <th key={c.key} className="tw-text-end" title={c.hint || undefined}>{c.label}</th>
              ))}
              <th aria-label="Open branch" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id}>
                <td className={NAMECOL}>
                  <span className="tw-block tw-font-medium">{r.name}</span>
                  {r.city && <span className="tw-block tw-text-[0.7rem] tw-text-content-muted">{r.city}</span>}
                </td>
                {COLUMNS.map((c) => {
                  const v = r[c.key];
                  const isBest = typeof v === "number" && v > 0 && v === best[c.key];
                  return (
                    <td key={c.key} className={`tw-text-end${isBest ? " tw-font-bold tw-text-success-fg" : ""}`}>
                      {fmt(v, c.format)}
                    </td>
                  );
                })}
                <td className="tw-text-end">
                  <button
                    type="button"
                    className="tw-cursor-pointer tw-rounded-[6px] tw-border-none tw-bg-transparent tw-px-1 tw-py-0.5 tw-leading-none tw-text-content-muted hover:tw-bg-surface-alt hover:tw-text-primary"
                    title={`View ${r.name} dashboard`}
                    onClick={() => selectBranch(r._id)}
                  >
                    <ArrowUpRight size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className={NAMECOL}>All branches</td>
              {COLUMNS.map((c) => (
                <td key={c.key} className="tw-text-end">
                  {/* Conversion is a ratio, so it is recomputed from the totals
                      rather than summed — adding percentages would be wrong. */}
                  {c.key === "conversionRate"
                    ? (totals.enquiries > 0
                      ? `${Math.round((totals.converted / totals.enquiries) * 100)}%`
                      : "—")
                    : fmt(totals[c.key], c.format)}
                </td>
              ))}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default BranchComparison;
